import { ethers } from "ethers";

/**
 * ENS lookup helpers.
 *
 * The protocol's primary chain (Base, Polygon, etc.) is rarely the ENS
 * registry chain, so all reverse lookups go through a dedicated
 * mainnet provider. The provider is constructed lazily and cached for
 * the lifetime of the page; results are memoised per address so the
 * same wallet doesn't trigger repeated network calls when the user
 * navigates between pages.
 */

// ENS reverse lookups run from the browser, so the mainnet RPC MUST
// send CORS headers — otherwise every lookup fails a preflight check
// and the browser logs a noisy `net::ERR_FAILED` even though our
// try/catch handles the rejection. `eth.llamarpc.com` does NOT allow
// browser origins; PublicNode does. Override via VITE_ENS_MAINNET_RPC
// only with another CORS-enabled endpoint.
const ENS_MAINNET_RPC =
    import.meta.env.VITE_ENS_MAINNET_RPC || "https://ethereum-rpc.publicnode.com";

let mainnetProvider = null;
const cache = new Map();

function getMainnetProvider() {
    if (!mainnetProvider) {
        mainnetProvider = new ethers.JsonRpcProvider(ENS_MAINNET_RPC);
    }
    return mainnetProvider;
}

/**
 * Resolves the primary ENS name for the given address. Returns `null`
 * when no name is set or the lookup fails. Per-address result is
 * cached so subsequent calls in the same session are free.
 */
export async function lookupEnsName(address) {
    if (!address) return null;
    const lower = address.toLowerCase();
    if (cache.has(lower)) return cache.get(lower);
    try {
        const name = await getMainnetProvider().lookupAddress(lower);
        cache.set(lower, name || null);
        return name || null;
    } catch {
        cache.set(lower, null);
        return null;
    }
}

/**
 * Returns either the resolved ENS name or a shortened `0x12…cdef`
 * fallback so UIs always have something stable to render. Useful in
 * navbars and position cards where we want ENS names when they exist
 * but a graceful fallback when they don't.
 */
export async function displayNameFor(address) {
    const ens = await lookupEnsName(address);
    if (ens) return ens;
    if (!address) return "";
    return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

/** Test-only helper — clears the in-memory cache between unit tests. */
export function _clearEnsCache() {
    cache.clear();
}
